import { useEffect, useState } from 'react';
import axios from 'axios';
import ContainerLayout from '../../../components/ContainerLayout';
import { parseEmbedLayoutConfig } from '../../../variables/embedConversationLayout';

const MAX_STEPS = 200;

// A Conversation's node graph is a real graph (branches, and even cycles
// for menu/FAQ-style conversations - see ConversationFlowService's own
// comment about conversation 86275 linking back and forth on purpose), not
// a straight line - there's no existing linearization anywhere else in the
// app (the canvas builder just lays out nodes by x/y and draws every edge).
// This walks ONE representative path (preferring each node's isRight
// connector, matching the "happy path" a correct-answering student would
// take) so the flattened preview has something concrete to scroll through -
// a deliberate simplification for branching content, not a full graph
// render. Every connector on a visited node is still shown as an option,
// only the *next node* choice is narrowed to one.
function pickFollowConnector(connectors) {
    if (!connectors || connectors.length === 0) return null;
    const right = connectors.find((c) => c.isRight && c.isRight.id === 1);
    if (right) return right;
    return connectors[0];
}

// Connectors embedded in a node's answerConnectorsFrom/answerConnectorsNext
// have their own fromId/nextId stripped by the backend (@JsonIgnoreProperties,
// to avoid infinite JSON recursion) - the only way to resolve "where does
// connector C lead" is to find the question whose answerConnectorsNext
// contains a connector with that same id. Mirrors
// conversations-creator-canvas/graph.js's findQuestionByNextConnector.
function findTargetByConnectorId(questions, connectorId) {
    return questions.find((q) => (q.answerConnectorsNext || []).some((c) => c.id === connectorId)) || null;
}

function flattenConversation(questions) {
    if (!questions || questions.length === 0) return [];
    const path = [];
    const visited = new Set();
    let current = questions[0]; // no explicit "start" flag anywhere - first array element is the convention the bot and the canvas builder both already use
    let steps = 0;
    while (current && steps < MAX_STEPS && !visited.has(current.id)) {
        visited.add(current.id);
        path.push(current);
        steps++;
        if (current.isLast && current.isLast.id === 1) break;
        const followConnector = pickFollowConnector(current.answerConnectorsFrom);
        if (!followConnector) break;
        current = findTargetByConnectorId(questions, followConnector.id);
    }
    return path;
}

// clasification.id === 1 means "type a free-text answer" (matches the
// bot's own ConversationFlowService.waitingAnswerHandlerByAnswerConnector
// switch) - anything else is a normal clickable option. A text-answer node
// only ever shows the input, no button options, same as the live bot.
function isTextAnswerNode(question) {
    return (question.answerConnectorsFrom || []).some((c) => c.clasification && c.clasification.id === 1);
}

function ConversationNodePreview({ question, index }) {
    const textAnswer = isTextAnswerNode(question);
    const [picked, setPicked] = useState(null);
    const [typed, setTyped] = useState('');
    const options = (question.answerConnectorsFrom || []).filter((c) => !(c.clasification && c.clasification.id === 1));

    return (
        <div className='course-preview-question'>
            <p className='course-preview-question-text'>{index + 1}. {question.text}</p>
            {textAnswer ? (
                <input type='text' className='course-preview-blank-input course-preview-conversation-input'
                    value={typed} onChange={(e) => setTyped(e.target.value)} placeholder='Введіть відповідь...' />
            ) : (
                <div className='course-preview-options'>
                    {options.map((c) => (
                        <label className='course-preview-option-row' key={c.id}>
                            <input type='radio' name={'conv-' + question.id} checked={picked === c.id} onChange={() => setPicked(c.id)} />
                            <span>{c.text}</span>
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
}

// "Тест в одному вікні" (displayMode:'stepper') - a single square window
// (width:100% of its slot, aspect-ratio:1 for the "height roughly equals
// width" ask) walking the same `path` one question at a time instead of
// showing every question at once - a "Далі" button advances in place,
// matching how the separate quiz webapp's own tests actually step through
// questions. Genuinely stateful (current index, current answer), unlike
// the layout modes which are pure CSS arrangement of already-rendered
// nodes - that's why this isn't just another ContainerLayout `layout`
// value.
function EmbedConversationStepper({ path }) {
    const [index, setIndex] = useState(0);
    const [picked, setPicked] = useState(null);
    const [typed, setTyped] = useState('');

    if (path.length === 0) return null;
    const finished = index >= path.length;
    const question = finished ? null : path[index];
    const textAnswer = question ? isTextAnswerNode(question) : false;
    const options = question ? (question.answerConnectorsFrom || []).filter((c) => !(c.clasification && c.clasification.id === 1)) : [];

    const handleNext = () => {
        setIndex((prev) => prev + 1);
        setPicked(null);
        setTyped('');
    };
    const handleRestart = () => {
        setIndex(0);
        setPicked(null);
        setTyped('');
    };

    return (
        <div className='course-preview-stepper'>
            {!finished ? (
                <>
                    <div className='course-preview-stepper-head'>
                        <div className='course-preview-stepper-progress-bar'>
                            <div className='course-preview-stepper-progress-fill' style={{ width: (index / path.length * 100) + '%' }} />
                        </div>
                        <div className='course-preview-stepper-progress'>Питання {index + 1} з {path.length}</div>
                    </div>
                    <div className='course-preview-stepper-body'>
                        <div className='course-preview-stepper-center'>
                            <p className='course-preview-question-text'>{question.text}</p>
                            {textAnswer ? (
                                <input type='text' className='course-preview-blank-input course-preview-conversation-input'
                                    value={typed} onChange={(e) => setTyped(e.target.value)} placeholder='Введіть відповідь...' />
                            ) : (
                                <div className='course-preview-options'>
                                    {options.map((c) => (
                                        <label className={'course-preview-option-row' + (picked === c.id ? ' course-preview-option-row-picked' : '')} key={c.id}>
                                            <input type='radio' name={'stepper-' + question.id} checked={picked === c.id} onChange={() => setPicked(c.id)} />
                                            <span>{c.text}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <button type='button' className='course-preview-stepper-next-btn' onClick={handleNext}>Далі</button>
                </>
            ) : (
                <div className='course-preview-stepper-finished'>
                    <p>Розмову завершено 🎉</p>
                    <button type='button' className='course-preview-stepper-next-btn' onClick={handleRestart}>Пройти ще раз</button>
                </div>
            )}
        </div>
    );
}

// Full flattened walkthrough of an embedded conversation/test - question
// after question with its options (or text input), shown all at once, no
// waiting on the previous one to be "answered" - matching the rest of the
// course preview's non-interactive-wait convention.
export default function EmbedConversationPreview({ block }) {
    const conversation = block.conversation;
    const conversationId = conversation ? conversation.id : null;
    const [questions, setQuestions] = useState(null);

    useEffect(() => {
        if (!conversationId) {
            setQuestions([]);
            return;
        }
        axios.get('/api/answers/?conversation=' + conversationId, { withCredentials: true })
            .then((response) => setQuestions(Array.isArray(response.data) ? response.data : []))
            .catch(() => setQuestions([]));
    }, [conversationId]);

    if (!conversation) return <span className='exercise-block-empty-hint'>Розмова не обрана</span>;
    if (questions === null) return <span className='exercise-block-empty-hint'>Завантаження...</span>;

    const path = flattenConversation(questions);
    const layoutConfig = parseEmbedLayoutConfig(block.config);
    const displayMode = layoutConfig.displayMode || 'layout';

    return (
        <div className='course-preview-embed-conversation'>
            {path.length === 0 ? (
                <span className='exercise-block-empty-hint'>Розмова порожня</span>
            ) : displayMode === 'stepper' ? (
                <EmbedConversationStepper path={path} />
            ) : (
                <ContainerLayout layout={layoutConfig.layout} config={layoutConfig}>
                    {path.map((q, i) => <ConversationNodePreview key={q.id} question={q} index={i} />)}
                </ContainerLayout>
            )}
        </div>
    );
}
